import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Search,
  WalletCards,
  X,
} from "lucide-react";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import { AppDispatch, RootState } from "../../../../store";
import { getWalletTransactions } from "../../../../Redux/wallet_apis/slice";
import { TransactionSource } from "../../../../Redux/wallet_apis/types";
import "./WithdrawalListPage.css";

type DateRange = {
  from: string;
  to: string;
};

const entriesOptions = [10, 25, 50, 75, 100];

const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const money = (value?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fromIsoDate = (value: string) => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date: Date, amount: number) =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

const sameDate = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isBetween = (date: Date, from: Date | null, to: Date | null) => {
  if (!from || !to) {
    return false;
  }

  const value = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
  const start = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate(),
  ).getTime();
  const end = new Date(
    to.getFullYear(),
    to.getMonth(),
    to.getDate(),
  ).getTime();

  return value > start && value < end;
};

const buildCalendarDays = (month: Date) => {
  const first = startOfMonth(month);
  const start = new Date(
    first.getFullYear(),
    first.getMonth(),
    1 - first.getDay(),
  );

  return Array.from(
    { length: 42 },
    (_, index) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + index),
  );
};

const monthTitle = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const formatRangeDate = (value: string) => {
  const date = fromIsoDate(value);

  if (!date) {
    return "Select date";
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDate = (value?: string) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = date
    .toLocaleDateString("en-US", { month: "short" })
    .toUpperCase();
  return `${day}-${month}-${date.getFullYear()}`;
};

type WithdrawalBucket = "success" | "pending" | "failed";

const statusBucket = (status?: string): WithdrawalBucket => {
  const normalized = (status || "pending").toLowerCase();
  if (normalized === "completed" || normalized === "success") {
    return "success";
  }
  if (
    normalized === "failed" ||
    normalized === "rejected" ||
    normalized === "cancelled"
  ) {
    return "failed";
  }
  return "pending";
};

const statusLabel = (status?: string) =>
  status === "COMPLETED"
    ? "Success"
    : (status || "Pending").charAt(0).toUpperCase() +
      (status || "Pending").slice(1).toLowerCase();

const incentiveName = (txn: any) =>
  txn.component === "REFERRAL_COMMISSION"
    ? "Referral Incentive"
    : txn.description || "Referral Incentive";

const StatusBadge = ({ status }: { status?: string }) => (
  <span className={`withdrawal-status withdrawal-status-${statusBucket(status)}`}>
    {statusLabel(status)}
  </span>
);

const CalendarMonth = ({
  month,
  range,
  onSelect,
}: {
  month: Date;
  range: DateRange;
  onSelect: (date: Date) => void;
}) => {
  const days = useMemo(() => buildCalendarDays(month), [month]);
  const from = fromIsoDate(range.from);
  const to = fromIsoDate(range.to);

  return (
    <div className="withdrawal-calendar-month">
      <div className="withdrawal-calendar-title">{monthTitle(month)}</div>

      <div className="withdrawal-calendar-weekdays">
        {weekDays.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div className="withdrawal-calendar-grid">
        {days.map((date) => {
          const outside = date.getMonth() !== month.getMonth();
          const isStart = Boolean(from && sameDate(date, from));
          const isEnd = Boolean(to && sameDate(date, to));
          const inRange = isBetween(date, from, to);

          return (
            <button
              key={toIsoDate(date)}
              type="button"
              className={[
                "withdrawal-calendar-day",
                outside ? "is-outside" : "",
                isStart ? "is-start" : "",
                isEnd ? "is-end" : "",
                inRange ? "is-range" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelect(date)}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WithdrawalDateRangePicker = ({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (value: DateRange) => void;
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const [leftMonth, setLeftMonth] = useState<Date>(
    startOfMonth(fromIsoDate(value.from) ?? new Date()),
  );

  useEffect(() => {
    const clickAway = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", clickAway);
    document.addEventListener("keydown", escape);

    return () => {
      document.removeEventListener("mousedown", clickAway);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  const openPicker = () => {
    setDraft(value);
    setLeftMonth(startOfMonth(fromIsoDate(value.from) ?? new Date()));
    setOpen((previous) => !previous);
  };

  const selectDate = (date: Date) => {
    const iso = toIsoDate(date);

    if (!draft.from || draft.to) {
      setDraft({ from: iso, to: "" });
      return;
    }

    if (iso < draft.from) {
      setDraft({ from: iso, to: draft.from });
      return;
    }

    setDraft({ from: draft.from, to: iso });
  };

  const clear = () => {
    const empty = { from: "", to: "" };
    setDraft(empty);
    onChange(empty);
    setOpen(false);
  };

  return (
    <div className="withdrawal-date-picker" ref={rootRef}>
      <button
        type="button"
        className={`withdrawal-date-trigger ${open ? "is-open" : ""}`}
        onClick={openPicker}
      >
        <CalendarDays size={17} />
        <span>
          {value.from && value.to
            ? `${formatRangeDate(value.from)} - ${formatRangeDate(value.to)}`
            : "Select custom date range"}
        </span>
        <ChevronDown size={15} />
      </button>

      {open && (
        <div className="withdrawal-date-popover">
          <div className="withdrawal-date-head">
            <div>
              <strong>Custom date range</strong>
              <span>Select a start date and an end date.</span>
            </div>

            <div className="withdrawal-date-nav">
              <button
                type="button"
                onClick={() =>
                  setLeftMonth((previous) => addMonths(previous, -1))
                }
              >
                <ChevronLeft size={17} />
              </button>
              <button
                type="button"
                onClick={() =>
                  setLeftMonth((previous) => addMonths(previous, 1))
                }
              >
                <ChevronRight size={17} />
              </button>
            </div>
          </div>

          <div className="withdrawal-date-calendars">
            <CalendarMonth
              month={leftMonth}
              range={draft}
              onSelect={selectDate}
            />
            <CalendarMonth
              month={addMonths(leftMonth, 1)}
              range={draft}
              onSelect={selectDate}
            />
          </div>

          <div className="withdrawal-date-summary">
            <div>
              <span>From</span>
              <strong>{formatRangeDate(draft.from)}</strong>
            </div>
            <div>
              <span>To</span>
              <strong>{formatRangeDate(draft.to)}</strong>
            </div>
          </div>

          <div className="withdrawal-date-actions">
            <button
              type="button"
              className="withdrawal-date-clear"
              onClick={clear}
            >
              <X size={14} />
              Clear
            </button>
            <button
              type="button"
              className="withdrawal-date-apply"
              disabled={Boolean(draft.from) && !draft.to}
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            >
              Apply range
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WithdrawlHistory = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { transactions, loading, pagination } = useSelector(
    (state: RootState) => state.wallet,
  );

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    from: "",
    to: "",
  });
  const [selected, setSelected] = useState<any | null>(null);

  useEffect(() => {
    dispatch(
      getWalletTransactions({
        page,
        limit,
        source: TransactionSource.WITHDRAWAL,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (selected) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }

    document.body.style.overflow = "";
    return undefined;
  }, [selected]);

  const records = (transactions || []).filter((txn: any) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      txn.transactionNumber?.toLowerCase().includes(q) ||
      txn.description?.toLowerCase().includes(q) ||
      incentiveName(txn).toLowerCase().includes(q) ||
      statusLabel(txn.status).toLowerCase().includes(q)
    );
  });

  const totalRecords = pagination?.total ?? records.length;

  const resetPage = () => setPage(1);

  return (
    <section className="withdrawal-page">
      <div className="withdrawal-shell">
        <header className="withdrawal-hero">
          <div className="withdrawal-hero-icon">
            <WalletCards size={24} />
          </div>

          <div className="withdrawal-hero-copy">
            <span className="withdrawal-eyebrow">MY WALLET</span>
            <h1>Withdrawal History</h1>
            <p>
              Track requested amounts, transferred payouts and payout notes
              in one place.
            </p>
          </div>

          <span className="withdrawal-record-count">
            {totalRecords} records
          </span>
        </header>

        <div className="withdrawal-toolbar">
          <div className="withdrawal-toolbar-left">
            <select
              value={limit}
              aria-label="Entries per page"
              onChange={(event) => {
                setLimit(Number(event.target.value));
                resetPage();
              }}
            >
              {entriesOptions.map((option) => (
                <option key={option} value={option}>
                  {option} Entries
                </option>
              ))}
            </select>

            <WithdrawalDateRangePicker
              value={dateRange}
              onChange={(value) => {
                setDateRange(value);
                resetPage();
              }}
            />
          </div>

          <label className="withdrawal-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              placeholder="Search withdrawals..."
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <div
              className="spinner-border spinner-border-sm text-dark"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="withdrawal-desktop-list">
              {records.length ? (
                records.map((txn: any, index: number) => (
                  <article
                    className="withdrawal-desktop-card"
                    key={txn.id}
                  >
                    <div className="withdrawal-record-header">
                      <div className="withdrawal-record-heading">
                        <span className="withdrawal-record-number">
                          Withdrawal #
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="withdrawal-record-date">
                          <CalendarDays size={15} />
                          <strong>
                            {formatDisplayDate(txn.createdAt)}
                          </strong>
                        </div>
                      </div>

                      <div className="withdrawal-record-header-actions">
                        <StatusBadge status={txn.status} />
                        <Link
                          className="withdrawal-record-download"
                          to={`/WithdrawlHistoryDetails/${txn.transactionNumber}`}
                        >
                          <Eye size={15} />
                          Details
                        </Link>
                        <Link
                          className="withdrawal-record-download"
                          to={`/CustRefPayInvoice/${txn.transactionNumber}`}
                        >
                          <Download size={15} />
                          Payout Note
                        </Link>
                      </div>
                    </div>

                    <div className="withdrawal-record-content">
                      <div className="withdrawal-record-main">
                        <div className="withdrawal-record-icon">
                          <WalletCards size={22} />
                        </div>
                        <div className="withdrawal-record-copy">
                          <span>Incentive Name</span>
                          <h3>{incentiveName(txn)}</h3>
                          <div className="withdrawal-record-transaction">
                            <span>W.Transaction No</span>
                            <strong>{txn.transactionNumber}</strong>
                          </div>
                        </div>
                      </div>

                      <div className="withdrawal-record-amount-grid">
                        <div className="withdrawal-amount-panel withdrawal-amount-requested">
                          <span>Requested Amount</span>
                          <strong>
                            {money(
                              Number(
                                txn.deductions?.grossAmount || txn.amount,
                              ),
                            )}
                          </strong>
                          <small>Withdrawal request</small>
                        </div>
                        <div className="withdrawal-amount-panel withdrawal-amount-transferred">
                          <span>Transferred Amount</span>
                          <strong>
                            {money(
                              Number(
                                txn.deductions?.netAmount || txn.amount,
                              ),
                            )}
                          </strong>
                          <small>
                            {statusBucket(txn.status) === "success"
                              ? "Amount transferred"
                              : statusBucket(txn.status) === "pending"
                                ? "Transfer pending"
                                : "Transfer not completed"}
                          </small>
                        </div>
                      </div>

                      <div className="withdrawal-record-note">
                        <div className="withdrawal-record-note-icon">
                          <FileText size={19} />
                        </div>
                        <div>
                          <span>Payout Note</span>
                          <strong>{txn.transactionNumber}</strong>
                          <small>
                            {formatDisplayDate(txn.createdAt)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <div className="withdrawal-empty">
                  No withdrawal records match the selected filters.
                </div>
              )}
            </div>

            <div className="withdrawal-mobile-list">
              {records.length ? (
                records.map((txn: any, index: number) => (
                  <button
                    type="button"
                    className="withdrawal-mobile-card"
                    key={`mobile-${txn.id}`}
                    onClick={() => setSelected(txn)}
                  >
                    <div className="withdrawal-mobile-top">
                      <span className="withdrawal-mobile-serial">
                        {index + 1}
                      </span>
                      <StatusBadge status={txn.status} />
                      <span className="withdrawal-mobile-date">
                        <CalendarDays size={13} />
                        {formatDisplayDate(txn.createdAt)}
                      </span>
                    </div>

                    <div className="withdrawal-mobile-main">
                      <div className="withdrawal-mobile-icon">
                        <WalletCards size={19} />
                      </div>
                      <div className="withdrawal-mobile-copy">
                        <strong>{incentiveName(txn)}</strong>
                        <span>{txn.transactionNumber}</span>
                      </div>
                      <ChevronRight size={19} />
                    </div>

                    <div className="withdrawal-mobile-bottom">
                      <div>
                        <span>Requested</span>
                        <strong>
                          {money(
                            Number(
                              txn.deductions?.grossAmount || txn.amount,
                            ),
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>Transferred</span>
                        <strong>
                          {money(
                            Number(
                              txn.deductions?.netAmount || txn.amount,
                            ),
                          )}
                        </strong>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="withdrawal-empty">
                  No withdrawal records match the selected filters.
                </div>
              )}
            </div>
          </>
        )}

        <div className="withdrawal-pagination">
          {pagination && pagination.totalPages > 1 && (
            <Pagenation
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </div>

      {selected && (
        <>
          <button
            type="button"
            aria-label="Close withdrawal details"
            className="withdrawal-sheet-overlay"
            onClick={() => setSelected(null)}
          />

          <aside
            className="withdrawal-sheet"
            aria-modal="true"
            role="dialog"
          >
            <div className="withdrawal-sheet-handle" />

            <div className="withdrawal-sheet-head">
              <div>
                <span className="withdrawal-eyebrow">
                  WITHDRAWAL DETAILS
                </span>
                <h2>{incentiveName(selected)}</h2>
              </div>
              <button
                type="button"
                className="withdrawal-sheet-close"
                onClick={() => setSelected(null)}
              >
                <X size={19} />
              </button>
            </div>

            <div className="withdrawal-sheet-status-row">
              <StatusBadge status={selected.status} />
              <span>
                <CalendarDays size={14} />
                {formatDisplayDate(selected.createdAt)}
              </span>
            </div>

            <div className="withdrawal-sheet-transaction">
              <div className="withdrawal-mobile-icon">
                <WalletCards size={21} />
              </div>
              <div>
                <span>W.Transaction No</span>
                <strong>{selected.transactionNumber}</strong>
              </div>
            </div>

            <div className="withdrawal-sheet-grid">
              <div>
                <span>Requested Amount</span>
                <strong>
                  {money(
                    Number(
                      selected.deductions?.grossAmount || selected.amount,
                    ),
                  )}
                </strong>
              </div>
              <div>
                <span>Transferred Amount</span>
                <strong>
                  {money(
                    Number(
                      selected.deductions?.netAmount || selected.amount,
                    ),
                  )}
                </strong>
              </div>
              <div>
                <span>Incentive Name</span>
                <strong>{incentiveName(selected)}</strong>
              </div>
              <div>
                <span>Payout Note No</span>
                <strong>{selected.transactionNumber}</strong>
              </div>
            </div>

            <div className="withdrawal-sheet-note">
              <div>
                <span>Payout Note</span>
                <strong>{selected.transactionNumber}</strong>
                <small>{formatDisplayDate(selected.createdAt)}</small>
              </div>
              <FileText size={22} />
            </div>

            <button
              type="button"
              className="withdrawal-sheet-download"
              onClick={() =>
                navigate(
                  `/CustRefPayInvoice/${selected.transactionNumber}`,
                )
              }
            >
              <Download size={17} />
              Download Payout Note
            </button>

            <button
              type="button"
              className="withdrawal-sheet-view"
              onClick={() =>
                navigate(
                  `/WithdrawlHistoryDetails/${selected.transactionNumber}`,
                )
              }
            >
              <Eye size={17} />
              Open Full Details
            </button>
          </aside>
        </>
      )}
    </section>
  );
};

export default WithdrawlHistory;
